import type { PaginatedList } from '@local/common-lib';
import type { AxiosInstance } from 'axios';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { AuthenticatedState } from '../utils/auth';
import type {
  CacheProviderProps,
  Caches,
  FetchAllCacheDefinition,
  FetchOneCacheDefinition,
  IdSerializer,
  WithId,
} from './use-cache.types.js';
import { Cache } from './use-cache.types.js';
import {
  compareAuthStates,
  createCaches,
  DEFAULT_ID_SERIALIZER,
} from './use-cache.util.js';

class FetchAllCache extends Cache {
  constructor(
    protected readonly idSerializer: IdSerializer,
    protected readonly fetchAll: () => Promise<WithId[]>,
  ) {
    super();
  }

  public async all<V extends WithId>(): Promise<V[]> {
    if (this.data === undefined) this.data = this.fetchData();

    return Array.from((await this.data).values()) as V[];
  }

  public async one<V extends WithId>(key: unknown): Promise<V | undefined> {
    if (this.data === undefined) this.data = this.fetchData();

    const data = await this.data;
    return data.get(this.idSerializer(key)) as V;
  }

  public invalidate(): void {
    this.data = undefined;
  }

  private fetchData = async (): Promise<Map<string, WithId>> => {
    const data = await this.fetchAll();
    return data.reduce((map: Map<string, WithId>, value) => {
      map.set(this.idSerializer(value._id), value);
      return map;
    }, new Map<string, WithId>());
  };
}

class FetchOneCache extends Cache {
  constructor(
    protected readonly initializer: () => Map<string, WithId>,
    protected readonly idSerializer: IdSerializer,
    protected readonly fetchOne: (id: string) => Promise<WithId | undefined>,
  ) {
    super();
  }

  public async one<V extends WithId>(key: unknown): Promise<V | undefined> {
    if (this.data === undefined) {
      this.data = Promise.resolve(this.initializer());
    }

    const data = await this.data;
    const idSerialized = this.idSerializer(key);
    const element = data.get(idSerialized);
    if (element) return element as V;

    const value = await this.fetchOne(idSerialized);
    if (value) {
      data.set(idSerialized, value);
    }

    return value as V;
  }

  public async invalidate(key: unknown): Promise<void> {
    if (this.data === undefined) return;
    (await this.data).delete(this.idSerializer(key));
  }
}

export const defineFetchAllCacheFor = <V,>(
  url: string,
  idSerializer: IdSerializer = DEFAULT_ID_SERIALIZER,
): FetchAllCacheDefinition<V> => {
  return {
    fetchAll: async (axios: AxiosInstance) => {
      console.log('Populating cache with all...', url);
      return axios
        .get<PaginatedList<V>>(
          `${import.meta.env.VITE_APP_SERVER_URL ?? ''}${url}`,
        )
        .then((response) => response.data.items);
    },
    idSerializer,
  };
};

export const defineFetchOneCacheFor = <V,>(
  url: string,
  idSerializer: IdSerializer = DEFAULT_ID_SERIALIZER,
): FetchOneCacheDefinition<V> => {
  const initializer = (): Map<string, V> => new Map<string, V>();
  return {
    fetchOne: async (axios: AxiosInstance, id: string) => {
      console.log('Populating cache with one...', url, id);
      return axios
        .get<V>(`${import.meta.env.VITE_APP_SERVER_URL ?? ''}${url}/${id}`, {
          validateStatus: (status) => [200, 404].includes(status),
        })
        .then((response) =>
          response.status === 200 ? response.data : undefined,
        );
    },
    idSerializer,
    initializer,
  };
};

interface ContextProps {
  fetchAllCaches: Caches<FetchAllCache>;
  fetchOneCaches: Caches<FetchOneCache>;
}

export const CacheContext = createContext<ContextProps>({
  fetchAllCaches: {} as Caches<FetchAllCache>,
  fetchOneCaches: {} as Caches<FetchOneCache>,
});

export const CacheProvider: FC<PropsWithChildren<CacheProviderProps>> = ({
  authState,
  children,
  fetchAllCaches: fetchAllDefinitions,
  fetchOneCaches: fetchOneDefinitions,
}) => {
  const [previousAuthState, setPreviousAuthState] =
    useState<AuthenticatedState>();
  const [previousCaches, setPreviousCaches] = useState<ContextProps>();

  const { fetchAllCaches, fetchOneCaches } = useMemo(() => {
    if (
      previousCaches &&
      previousAuthState &&
      compareAuthStates(authState, previousAuthState)
    ) {
      return previousCaches;
    }

    const currentCaches = {
      fetchAllCaches: createCaches(
        authState,
        fetchAllDefinitions,
        createFetchAllCache,
      ),
      fetchOneCaches: createCaches(
        authState,
        fetchOneDefinitions,
        createFetchOneCache,
      ),
    };

    setPreviousAuthState(authState);
    setPreviousCaches(currentCaches);
    return currentCaches;
  }, [
    authState,
    previousAuthState,
    previousCaches,
    fetchAllDefinitions,
    fetchOneDefinitions,
  ]);

  return (
    <CacheContext.Provider value={{ fetchAllCaches, fetchOneCaches }}>
      {children}
    </CacheContext.Provider>
  );
};

const createFetchAllCache = (
  definition: FetchAllCacheDefinition<WithId>,
  authState: AuthenticatedState,
): FetchAllCache => {
  return new FetchAllCache(
    (id: unknown) => definition.idSerializer(id),
    () => definition.fetchAll(authState.axios),
  );
};

const createFetchOneCache = (
  definition: FetchOneCacheDefinition<WithId>,
  authState: AuthenticatedState,
): FetchOneCache => {
  return new FetchOneCache(
    () => definition.initializer(),
    (id: unknown) => definition.idSerializer(id),
    (id: string) => definition.fetchOne(authState.axios, id),
  );
};

export const useFetchAllCache = (type: string): FetchAllCache => {
  const { fetchAllCaches } = useContext(CacheContext);
  if (!(type in fetchAllCaches)) {
    throw new Error(`Fetch all cache for ${type} is not defined`);
  }

  return fetchAllCaches[type];
};

export const useFetchOneCache = (type: string): FetchOneCache => {
  const { fetchOneCaches } = useContext(CacheContext);
  if (!(type in fetchOneCaches)) {
    throw new Error(`Fetch one cache for ${type} is not defined`);
  }

  return fetchOneCaches[type];
};
